import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BillingAPI } from "@/lib/api";
import { useAuth } from "@/lib/providers";
import { toast } from "sonner";
import Seo from "@/components/Seo";

const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

async function loadRazorpay() {
  if (window.Razorpay) return true;
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const inr = (paise) => `₹${(paise / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function Pricing() {
  const [bundles, setBundles] = useState([]);
  const [keyId, setKeyId] = useState("");
  const [freeTrialCredits, setFreeTrialCredits] = useState(0);
  const [busy, setBusy] = useState(null);
  const [wallet, setWallet] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await BillingAPI.bundles();
        setBundles(data.bundles || []);
        setKeyId(data.razorpay_key_id || "");
        setFreeTrialCredits(data.free_trial_credits || 0);
        if (user) {
          const w = await BillingAPI.wallet();
          setWallet(w.data.wallet);
        }
      } catch (e) {
        toast.error("Could not load pricing");
      }
    })();
  }, [user]);

  const onBuy = async (bundle) => {
    if (!user) {
      // Send them to login; come back to /pricing.
      sessionStorage.setItem("pendingBundle", bundle.bundle_id);
      toast("Sign in first — you'll come right back to pay.");
      navigate("/");
      return;
    }
    setBusy(bundle.bundle_id);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load Razorpay checkout — check your connection.");
      const { data } = await BillingAPI.checkout(bundle.bundle_id);
      const opts = {
        key: data.razorpay_key_id || keyId,
        amount: data.amount_paise,
        currency: data.currency,
        order_id: data.order_id,
        name: "Verdict · Therefore Design",
        description: `${bundle.name} — ${bundle.credits} audit credits`,
        prefill: data.prefill || {},
        theme: { color: "#06B6D4" },
        handler: async (resp) => {
          try {
            const verify = await BillingAPI.verify({
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`+${verify.data.credits_added} audit credits added. New balance: ${verify.data.wallet.audit_credits}.`);
            setWallet(verify.data.wallet);
            // External users — send them straight to the dashboard so they
            // can run their first audit immediately.
            setTimeout(() => navigate("/studio"), 800);
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed. Contact support.");
          } finally {
            setBusy(null);
          }
        },
        modal: { ondismiss: () => setBusy(null) },
      };
      const rp = new window.Razorpay(opts);
      rp.on("payment.failed", (r) => {
        toast.error(`Payment failed: ${r?.error?.description || "Unknown error"}`);
        setBusy(null);
      });
      rp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Checkout failed");
      setBusy(null);
    }
  };

  return (
    <div className="v-container" style={{ paddingTop: 80, paddingBottom: 120, maxWidth: 1100 }}>
      <Seo
        path="/pricing"
        title="Pricing — pay-per-audit for Verdict"
        description="Simple pay-per-audit pricing for external designers and brand teams. First audit free for new signups. Buy audit credits in INR via Razorpay. Bulk bundles available."
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Verdict Audit Credits",
            description:
              "Pay-per-audit access to Verdict — the AI-powered packaging intelligence platform. Each credit runs one full 7-dimension packaging audit and produces a branded PDF report.",
            image: "https://verdict.thereforedesign.co.in/og-verdict.png",
            brand: { "@id": "https://verdict.thereforedesign.co.in/#organization" },
            offers: {
              "@type": "AggregateOffer",
              priceCurrency: "INR",
              lowPrice: "0",
              highPrice: "1499",
              offerCount: "3",
              availability: "https://schema.org/InStock",
              url: "https://verdict.thereforedesign.co.in/pricing",
            },
          },
        ]}
      />
      <div className="overline" style={{ color: "var(--v-accent)" }}>Pricing</div>
      <h1 style={{ fontSize: 48, marginTop: 8, lineHeight: 1.1 }}>
        Buy audit credits — pay only for what you run.
      </h1>
      <p style={{ color: "var(--v-text-2)", maxWidth: 640, marginTop: 18, fontSize: 16 }}>
        Each audit credit covers one full 7-dimension assessment or one variation pack inside a comparison.
        Credits never expire. Cancel anytime — nothing recurs.
      </p>

      {/* PROMINENT free-trial hero — only shown if a trial is configured AND
          the user hasn't already burned through it. We display to logged-out
          users (signup driver) and to logged-in users with no transactions
          yet. Designed to feel like a generous bonus, not a marketing trick. */}
      {freeTrialCredits > 0 && !wallet?.lifetime_credits_purchased && (
        <div
          data-testid="free-trial-banner"
          style={{
            marginTop: 28,
            padding: "20px 26px",
            border: "1.5px solid var(--v-accent)",
            borderRadius: 12,
            background: "linear-gradient(135deg, rgba(6,182,212,0.12), rgba(6,182,212,0.03))",
            display: "flex",
            alignItems: "center",
            gap: 22,
            flexWrap: "wrap",
          }}
        >
          <div style={{
            width: 52, height: 52, borderRadius: "50%",
            background: "var(--v-accent)", color: "#0A0A0A",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 22, fontWeight: 700, flexShrink: 0,
          }} aria-hidden>
            {freeTrialCredits}
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: 11, color: "var(--v-accent)", letterSpacing: ".14em", textTransform: "uppercase", fontWeight: 700 }}>
              First audit on us
            </div>
            <div style={{ fontSize: 18, marginTop: 4, color: "var(--v-text)" }}>
              {user ? (
                wallet && wallet.audit_credits > 0
                  ? <>You have <b>{wallet.audit_credits} free credit{wallet.audit_credits === 1 ? "" : "s"}</b> waiting — go run your first audit before topping up.</>
                  : <>Your free trial credit{freeTrialCredits === 1 ? "" : "s"} {freeTrialCredits === 1 ? "is" : "are"} ready in your wallet. Run your first audit, then top up only if it convinces you.</>
              ) : (
                <>Sign up with Google and get <b>{freeTrialCredits} free audit credit{freeTrialCredits === 1 ? "" : "s"}</b> auto-loaded to your wallet — no card needed. Try Verdict on your own pack before paying a paisa.</>
              )}
            </div>
          </div>
          {!user && (
            <button
              className="v-btn"
              style={{ background: "var(--v-accent)", color: "#0A0A0A", whiteSpace: "nowrap" }}
              onClick={() => navigate("/")}
              data-testid="free-trial-cta"
            >
              Claim your free audit →
            </button>
          )}
          {user && wallet && wallet.audit_credits > 0 && (
            <button
              className="v-btn"
              style={{ background: "var(--v-accent)", color: "#0A0A0A", whiteSpace: "nowrap" }}
              onClick={() => navigate("/projects/new")}
              data-testid="free-trial-run-audit"
            >
              Run your first audit →
            </button>
          )}
        </div>
      )}

      {wallet && (
        <div style={{ marginTop: 32, padding: "12px 18px", border: "1px solid var(--v-border)", borderRadius: 8, display: "inline-flex", alignItems: "center", gap: 12 }} data-testid="pricing-current-balance">
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: wallet.audit_credits > 0 ? "#06B6D4" : "#ef4444" }} />
          <span style={{ fontSize: 13, color: "var(--v-text-2)" }}>Your balance:</span>
          <b style={{ fontSize: 14 }}>{wallet.audit_credits} audit credit{wallet.audit_credits === 1 ? "" : "s"}</b>
        </div>
      )}

      <div id="bundles" style={{ marginTop: 48, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}>
        {bundles.map((b, idx) => {
          const isHighlighted = idx === 1; // middle tier visually emphasized
          return (
            <div
              key={b.bundle_id}
              data-testid={`bundle-${b.bundle_id}`}
              className="v-surface"
              style={{
                padding: 28,
                borderColor: isHighlighted ? "var(--v-accent)" : "var(--v-border)",
                borderWidth: isHighlighted ? 2 : 1,
                position: "relative",
              }}
            >
              {isHighlighted && (
                <div style={{ position: "absolute", top: -12, left: 24, padding: "4px 10px", background: "var(--v-accent)", color: "#0A0A0A", fontSize: 10, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase" }}>
                  Most popular
                </div>
              )}
              <div className="overline">{b.name}</div>
              <div style={{ marginTop: 18, fontSize: 44, fontWeight: 300, letterSpacing: "-.02em" }}>
                {inr(b.amount_paise)}
              </div>
              <div style={{ fontSize: 13, color: "var(--v-text-2)", marginTop: 6 }}>
                <b style={{ color: "var(--v-text)" }}>{b.credits}</b> audit credits ·
                ₹{Math.round(b.amount_paise / b.credits / 100)} per audit
              </div>
              <p style={{ color: "var(--v-text-2)", marginTop: 16, fontSize: 13, minHeight: 38 }}>
                {b.blurb}
              </p>
              <button
                className="v-btn"
                style={{ marginTop: 22, width: "100%", background: isHighlighted ? "var(--v-accent)" : undefined, color: isHighlighted ? "#0A0A0A" : undefined }}
                onClick={() => onBuy(b)}
                disabled={busy === b.bundle_id}
                data-testid={`buy-${b.bundle_id}-btn`}
              >
                {busy === b.bundle_id ? "Opening checkout…" : (user ? "Buy now" : "Sign in to buy")}
              </button>
            </div>
          );
        })}
      </div>

      <p style={{ marginTop: 48, color: "var(--v-text-2)", fontSize: 12, maxWidth: 700, lineHeight: 1.6 }}>
        Payments are processed by Razorpay (PCI-DSS compliant). All amounts in INR. GST invoice on request.
        Studio designers (@thereforedesign.co.in) don't need to buy credits — your studio handles billing internally.
      </p>
    </div>
  );
}
